/**
 * Scheda allenamento (V13), la scheda Riepilogo del guscio: note (nella
 * testata) e una card con i conteggi delle presenze e il piano della seduta
 * per gruppo; la presenza di ciascuno sta nell'Appello. I comandi (modifica,
 * stato, eliminazione) sono nella testata del guscio, in tutte le schede.
 */

import { Link, useParams } from 'react-router-dom';
import {
  CircleHelpIcon,
  ClockIcon,
  InfoIcon,
  LayersIcon,
  type LucideIcon,
  UsersIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDati } from '../comuni/dati.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { CardVista } from '../comuni/Riquadri.js';
import { useAllenamento } from './contesto.js';
import { type ComposizioneSeduta, ICONA_PRESENZA, formatoOra, riepiloga } from './allenamenti.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

export function SchedaAllenamento() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const allenamento = useAllenamento();
  const piano = useDati<ComposizioneSeduta>(id ? `/allenamenti/${id}/composizione` : '');

  if (!allenamento) return <StatoCaricamento errore={null} />;
  const { allenamento: a, rosa, partiteStessoGiorno } = allenamento.dettaglio;
  const r = riepiloga(rosa);

  const gruppi = piano.dati?.gruppi ?? [];
  const blocchi = piano.dati?.blocchi ?? [];

  return (
    <>
      {a.stato === 'annullato' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Allenamento annullato</AlertTitle>
          <AlertDescription>
            Non conteggia assenze nelle statistiche, e l'appello non si modifica finché non lo
            ripristini.
          </AlertDescription>
        </Alert>
      )}
      {partiteStessoGiorno.map((p) => (
        <Alert key={p.id}>
          <InfoIcon />
          <AlertTitle>Partita lo stesso giorno</AlertTitle>
          <AlertDescription>
            Lo stesso giorno c'è la partita{' '}
            <Link to={`/sq/${ss}/partite/${p.id}`} className="underline underline-offset-4">
              {p.inCasa ? 'in casa' : 'in trasferta'} con {p.avversario}
            </Link>{' '}
            alle {formatoOra.format(new Date(p.dataOra))}.
          </AlertDescription>
        </Alert>
      ))}

      <CardVista titolo="Piano della seduta" Icona={LayersIcon} conteggio={blocchi.length}>
        <dl className="grid grid-cols-4 gap-2">
          <Conteggio etichetta="Presenti" valore={r.presenti} Icona={ICONA_PRESENZA.presente} />
          <Conteggio etichetta="Assenti" valore={r.assenti} Icona={ICONA_PRESENZA.assente} />
          <Conteggio
            etichetta="Infortunati"
            valore={r.infortunati}
            Icona={ICONA_PRESENZA.infortunato}
          />
          <Conteggio etichetta="Non rilevati" valore={r.nonRilevati} Icona={CircleHelpIcon} />
        </dl>
        {!piano.dati ? (
          <StatoCaricamento errore={piano.errore} />
        ) : gruppi.length === 0 ? (
          <StatoVuoto
            titolo="Nessun gruppo"
            descrizione="Aprendo Componi si creano i quattro gruppi di sistema."
          />
        ) : (
          <ul className="grid gap-2 @3xl:grid-cols-2">
            {gruppi.map((g) => {
              const suoi = blocchi.filter((b) => b.gruppoIds.includes(g.id));
              return (
                <li
                  key={g.id}
                  className="flex flex-col gap-1 rounded-lg border border-border bg-card p-2.5"
                >
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-semibold">{g.nome}</span>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <UsersIcon aria-hidden className="size-4" />
                      <span className="font-medium text-foreground tabular-nums">
                        {g.giocatori.length}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <ClockIcon aria-hidden className="size-4" />
                      <span className="font-medium text-foreground tabular-nums">
                        {suoi.reduce((t, b) => t + b.minutiProgrammati, 0)}′
                      </span>
                    </span>
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {suoi.length === 0 ? 'Nessun blocco' : suoi.map((b) => b.nome).join(' · ')}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardVista>
    </>
  );
}

function Conteggio({
  etichetta,
  valore,
  Icona,
}: {
  etichetta: string;
  valore: number;
  Icona: LucideIcon;
}) {
  return (
    // Su telefono l'etichetta non ci sta: resta l'icona, col nome al passaggio.
    <div
      title={etichetta}
      className="flex flex-col items-center gap-0.5 rounded-lg border border-border bg-card p-2 text-center"
    >
      <dt className="flex items-center gap-1 text-[12px] text-muted-foreground">
        <Icona aria-hidden className="size-4 shrink-0 @xl:size-3.5" />
        <span className="sr-only @xl:not-sr-only @xl:truncate">{etichetta}</span>
      </dt>
      <dd className="text-xl font-bold tabular-nums">{valore}</dd>
    </div>
  );
}
